import React, { useState, useRef, useEffect, useCallback, useMemo, useLayoutEffect } from 'react';
import { toast } from 'react-toastify';
import Modal, { Field, inputStyle } from './Modal';
import { apiFetch } from '../hooks/useApi';
import { storageGet, storageSet, storageRemove } from '../utils/storage';
import { todayLocalISO, formatDateTime } from '../utils/dates';
import RepeatingListInput, { REPEATING_TYPE, columnsOf, filledRows, repeatingSummary } from './RepeatingList';

// Default grade bands when a client has no configured scale (Settings → Grading scales).
const DEFAULT_BANDS = [{ label: 'Pass', min: 50 }, { label: 'Fail', min: 0 }];
// Map a numeric score to a grade band (highest band whose min ≤ score).
const gradeFor = (score, bands) => {
  const n = Number(score); if (!Number.isFinite(n)) return '';
  const ordered = [...(bands && bands.length ? bands : DEFAULT_BANDS)].sort((a, b) => Number(b.min) - Number(a.min));
  const hit = ordered.find((b) => n >= Number(b.min));
  return (hit || ordered[ordered.length - 1] || {}).label || '';
};

/** Default fields for a template that has no builder-defined field list yet. */
const DEFAULT_FIELDS = [
  { id: 'f0', label: 'Result', type: 'Dropdown', options: ['Pass', 'Fail', 'N/A'] },
  { id: 'f1', label: 'Notes / observations', type: 'Text' },
  { id: 'f2', label: 'Photo evidence', type: 'Photo' },
];

/* ── Stable field ids ───────────────────────────────────────────────────────
 * Conditions (showWhen), formulas ({…}) and answers used to reference a field by
 * its LABEL, so renaming "Result" to "Outcome" silently broke every rule that
 * pointed at it. Every field now carries an `id`:
 *   - templates saved before ids existed get a deterministic one (`f<index>`),
 *     so the same template always produces the same ids;
 *   - label references in showWhen / formulas are migrated to ids on load;
 *   - lookups still fall back to the label, so anything not yet migrated works.
 */
const newFieldId = () => `fld_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;
export const makeFieldId = newFieldId;

/** Find a field by id, falling back to its label (legacy references). */
export const resolveField = (fields, ref) => {
  if (ref == null || ref === '') return null;
  const r = String(ref);
  return fields.find((f) => f.id === r)
    || fields.find((f) => (f.label || '') === r)
    || fields.find((f) => (f.label || '').trim().toLowerCase() === r.trim().toLowerCase())
    || null;
};

/** Rewrite {Label} references in a formula to {id}. Unknown names are left as written. */
export const formulaToIds = (formula, fields) => String(formula || '').replace(/\{([^}]+)\}/g, (m, name) => {
  const f = resolveField(fields, name.trim());
  return f ? `{${f.id}}` : m;
});
/** Rewrite {id} references back to {Label} for editing in the builder. */
export const formulaToLabels = (formula, fields) => String(formula || '').replace(/\{([^}]+)\}/g, (m, ref) => {
  const f = fields.find((x) => x.id === ref.trim());
  return f && (f.label || '').trim() ? `{${f.label.trim()}}` : m;
});

const migrateCondition = (c, fields) => {
  if (!c || !c.field) return c;
  const f = resolveField(fields, c.field);
  return f ? { ...c, field: f.id } : c;
};
const migrateShowWhen = (sw, fields) => {
  if (!sw) return sw;
  if (Array.isArray(sw.conditions)) return { ...sw, conditions: sw.conditions.map((c) => migrateCondition(c, fields)) };
  return migrateCondition(sw, fields);
};

/** Give every field a stable id and migrate label references to ids. Pure. */
export const normaliseFields = (raw) => {
  const list = (Array.isArray(raw) ? raw : []).filter((f) => f && typeof f === 'object');
  const seen = new Set();
  const withIds = list.map((f, i) => {
    let id = f.id != null && String(f.id).trim() ? String(f.id) : `f${i}`;
    while (seen.has(id)) id = `${id}_${i}`;
    seen.add(id);
    return { ...f, id };
  });
  return withIds.map((f) => ({
    ...f,
    ...(f.showWhen ? { showWhen: migrateShowWhen(f.showWhen, withIds) } : {}),
    ...(f.formula ? { formula: formulaToIds(f.formula, withIds) } : {}),
  }));
};

/** template.fields may arrive as a JSON string, an array, or be absent. */
export const parseFields = (t) => {
  let f = t?.fields;
  if (typeof f === 'string') { try { f = JSON.parse(f); } catch { f = null; } }
  return normaliseFields(Array.isArray(f) && f.length ? f : DEFAULT_FIELDS);
};

/** Old submissions keyed answers by field INDEX ({0: 'Pass'}). Re-key them by id. */
const rekeyValues = (vals, fields) => {
  if (!vals || typeof vals !== 'object') return {};
  const out = {};
  Object.entries(vals).forEach(([k, v]) => {
    if (k === '__flags') return;
    if (/^\d+$/.test(k) && fields[Number(k)]) out[fields[Number(k)].id] = v;
    else out[k] = v;
  });
  return out;
};

const normOptions = (opts) => {
  if (Array.isArray(opts)) return opts.filter(Boolean);
  if (typeof opts === 'string') return opts.split(',').map((o) => o.trim()).filter(Boolean);
  return [];
};

export const isImageData = (v) => typeof v === 'string' && v.startsWith('data:image/');
/** Photo answers are a list of image data URLs (older submissions stored one string). */
const photoList = (v) => (Array.isArray(v) ? v.filter(isImageData) : (isImageData(v) ? [v] : []));
/** Signature answers are { image, name, role, signedAt, method } (older ones were a bare data URL). */
const sigOf = (v) => (v && typeof v === 'object' && !Array.isArray(v) ? v : (isImageData(v) ? { image: v } : {}));

const isEmptyFor = (f, v) => {
  if (f.type === 'Photo') return photoList(v).length === 0;
  if (f.type === REPEATING_TYPE) return filledRows(v, columnsOf(f)).length === 0;
  if (f.type === 'Signature') return !sigOf(v).image;
  return v == null || v === '' || (Array.isArray(v) && v.length === 0);
};
const displayValue = (v) => (Array.isArray(v) ? v.join(', ') : (v ?? ''));

/* ── Priority tagging on findings ───────────────────────────────────────────
 * A field can declare the answer that constitutes a finding (`flagWhen`); when
 * the engineer hits it the form asks for a priority and a note there and then,
 * rather than leaving it buried in free text.
 *
 * Levels come from the client's own scale where one is configured (they differ
 * per client — A–E, 1–5, 1–4 with residual), falling back to a plain 1–4. */
export const DEFAULT_PRIORITY_LEVELS = [
  { code: 'P1', label: 'P1 — Immediate', colour: '#dc2626' },
  { code: 'P2', label: 'P2 — Urgent', colour: '#ea580c' },
  { code: 'P3', label: 'P3 — Planned', colour: '#b45309' },
  { code: 'P4', label: 'P4 — Advisory', colour: '#0e7490' },
];
/** Does this answer trip the field's flag condition? */
export const isFlagged = (field, value) => {
  const c = field?.flagWhen;
  if (!c) return false;
  const target = String(typeof c === 'object' ? c.value : c).trim().toLowerCase();
  if (!target) return false;
  if (Array.isArray(value)) return value.some((v) => String(v).trim().toLowerCase() === target);
  return String(value ?? '').trim().toLowerCase() === target;
};
// Attachments are data URLs with the original filename kept in the fragment, so a
// submission stays self-contained and still shows the file it actually was.
export const FILE_MAX_BYTES = 8 * 1024 * 1024;
export const MAX_PHOTOS_PER_FIELD = 10;
// The submit endpoint accepts 25 MB of JSON; leave headroom for the rest of the body.
const MAX_SUBMISSION_BYTES = 22 * 1024 * 1024;
export const fileNameOf = (v) => {
  const m = typeof v === 'string' && v.match(/#name=([^&]+)$/);
  try { return m ? decodeURIComponent(m[1]) : ''; } catch { return ''; }
};

// ── Conditional visibility + calculated fields ──
const valueByRef = (fields, values, ref) => { const f = resolveField(fields, ref); return f ? values[f.id] : undefined; };
/** Does one condition {field, op, value} hold against the current answers? */
const testCondition = (c, fields, values) => {
  if (!c || !c.field) return true;
  const v = valueByRef(fields, values, c.field);
  const target = String(c.value ?? '').trim().toLowerCase();
  const asText = Array.isArray(v) ? v.map((x) => String(x).trim().toLowerCase()) : [String(v ?? '').trim().toLowerCase()];
  const has = (t) => asText.some((x) => x === t);
  const num = Number(Array.isArray(v) ? v.length : v);
  const tnum = Number(c.value);
  switch (c.op || 'is') {
    case 'not': return !has(target);
    case 'contains': return asText.some((x) => x.includes(target));
    case 'any': return asText.some((x) => x !== '');          // answered at all
    case 'empty': return asText.every((x) => x === '');
    case 'gt': return Number.isFinite(num) && Number.isFinite(tnum) && num > tnum;
    case 'lt': return Number.isFinite(num) && Number.isFinite(tnum) && num < tnum;
    case 'is':
    default: return has(target);
  }
};

/**
 * A field shows unless its rule is unmet. `showWhen` accepts either a single
 * condition or { match: 'all' | 'any', conditions: [...] }.
 */
export const fieldVisible = (f, fields, values) => {
  const c = f.showWhen;
  if (!c) return true;
  if (Array.isArray(c.conditions)) {
    const live = c.conditions.filter((x) => x && x.field);
    if (!live.length) return true;
    return c.match === 'any'
      ? live.some((x) => testCondition(x, fields, values))
      : live.every((x) => testCondition(x, fields, values));
  }
  return testCondition(c, fields, values);
};
// Evaluate a formula like "{f3} * {f4}" (or legacy "{Height} * {Width}") — each reference
// becomes that field's number (multi-select → count). Sanitised to arithmetic only.
const evalFormula = (formula, fields, values) => {
  if (!formula) return '';
  const sub = String(formula).replace(/\{([^}]+)\}/g, (_, name) => {
    const v = valueByRef(fields, values, name.trim());
    const n = Number(Array.isArray(v) ? v.length : v);
    return Number.isFinite(n) ? String(n) : '0';
  });
  if (!/^[0-9.+\-*/()\s]+$/.test(sub)) return '';
  // eslint-disable-next-line no-new-func -- input is sanitised above to arithmetic characters only
  try { const r = Function(`"use strict";return (${sub})`)(); return Number.isFinite(r) ? String(Math.round(r * 100) / 100) : ''; } catch { return ''; }
};

const loadImage = (src) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => resolve(img);
  img.onerror = reject;
  img.src = src;
});
const readAsDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

// Downscale + compress a photo to a JPEG data URL (longest side ≤ 1600 px, quality 0.8).
// A phone photo is 3–8 MB; this keeps it to a few hundred KB so a form with several
// photos stays under the upload limit instead of failing with "too large".
export const compressImage = async (file, maxDim = 1600, quality = 0.8) => {
  const img = await loadImage(await readAsDataUrl(file));
  let { width, height } = img;
  if (width > maxDim || height > maxDim) {
    const s = Math.min(maxDim / width, maxDim / height);
    width = Math.round(width * s); height = Math.round(height * s);
  }
  const canvas = document.createElement('canvas');
  canvas.width = width; canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#ffffff';            // transparent PNGs would turn black as JPEG
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(img, 0, 0, width, height);
  return canvas.toDataURL('image/jpeg', quality);
};

// Prefill fields from job context (label-matched) so an engineer isn't re-typing
// the job, site, date and their own name on every form.
const seedFromContext = (fields, context) => {
  const out = {};
  if (!context) return out;
  const today = todayLocalISO();
  fields.forEach((f) => {
    if (f.type === 'Section') return;
    const l = String(f.label || '').toLowerCase();
    if (f.type === 'Date' && /date/.test(l) && !/(next|due|expiry|expire|follow|return|leave|start|end|preferred)/.test(l)) out[f.id] = context.date || today;
    else if (f.type === 'Text' || f.type === 'Dropdown') {
      if (/\b(job|work order|works)\b/.test(l) && context.job) out[f.id] = context.job;
      else if (/(site|property|building|location|address|premises)/.test(l) && context.property) out[f.id] = context.property;
      else if (/(engineer|operative|technician|inspector|surveyor|carried out|completed by|your name)/.test(l) && context.engineer) out[f.id] = context.engineer;
    }
  });
  return out;
};

const prefersReducedMotion = () => {
  try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return false; }
};

/* ── Signature ──────────────────────────────────────────────────────────────
 * Drawn on a canvas sized to its on-screen box × devicePixelRatio, so ink lands
 * under the finger at any width and stays crisp on high-DPI phones. Pointer
 * events cover mouse, pen and touch; touch-action:none stops the page scrolling
 * while signing. A typed signature is the keyboard-accessible alternative
 * (WCAG 2.1.1). Either way the signer's name, role and the time are recorded. */
const SIG_H = 150;
const TYPED_FONT = 'italic 34px "Segoe Script", "Brush Script MT", "Apple Chancery", cursive';

const renderTypedSignature = (text) => {
  const c = document.createElement('canvas');
  const dpr = 2;
  c.width = 520 * dpr; c.height = SIG_H * dpr;
  const ctx = c.getContext('2d');
  if (!ctx) return '';
  ctx.scale(dpr, dpr);
  ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, 520, SIG_H);
  ctx.fillStyle = '#111827';
  ctx.font = TYPED_FONT;
  ctx.textBaseline = 'middle';
  ctx.fillText(String(text).slice(0, 60), 20, SIG_H / 2, 480);
  ctx.strokeStyle = '#9ca3af'; ctx.lineWidth = 1;
  ctx.beginPath(); ctx.moveTo(20, SIG_H - 28); ctx.lineTo(500, SIG_H - 28); ctx.stroke();
  return c.toDataURL('image/png');
};

const SignaturePad = ({ value, onChange, invalid, id, describedBy }) => {
  const sig = sigOf(value);
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });
  const inked = useRef(false);
  const [mode, setMode] = useState(sig.method === 'typed' ? 'type' : 'draw');
  const [typed, setTyped] = useState(sig.method === 'typed' ? (sig.typedText || sig.name || '') : '');
  const sigRef = useRef(sig);
  sigRef.current = sig;

  const update = (patch) => onChange({ ...sigRef.current, ...patch });

  // Size the canvas backing store to the displayed box × DPR and redraw any existing ink.
  const setup = useCallback(() => {
    const c = canvasRef.current;
    if (!c) return;
    const rect = c.getBoundingClientRect();
    const dpr = Math.max(1, window.devicePixelRatio || 1);
    const w = Math.max(1, Math.round(rect.width * dpr));
    const h = Math.max(1, Math.round(SIG_H * dpr));
    if (c.width === w && c.height === h) return;
    c.width = w; c.height = h;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, w, h);
    const img = sigRef.current.method !== 'typed' ? sigRef.current.image : null;
    if (img) loadImage(img).then((im) => { ctx.drawImage(im, 0, 0, w, h); }).catch(() => {});
  }, []);

  useLayoutEffect(() => {
    if (mode !== 'draw') return undefined;
    setup();
    if (typeof ResizeObserver === 'undefined' || !wrapRef.current) return undefined;
    const ro = new ResizeObserver(() => setup());
    ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, [mode, setup]);

  // Pointer position in canvas backing-store pixels (scaled for CSS size vs pixel size).
  const pos = (e) => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    return { x: (e.clientX - r.left) * (c.width / r.width), y: (e.clientY - r.top) * (c.height / r.height) };
  };
  const ctxOf = () => {
    const c = canvasRef.current;
    const ctx = c.getContext('2d');
    const dpr = c.width / Math.max(1, c.getBoundingClientRect().width);
    ctx.lineWidth = 2.2 * dpr; ctx.lineCap = 'round'; ctx.lineJoin = 'round'; ctx.strokeStyle = '#111827';
    return ctx;
  };
  const start = (e) => {
    if (e.button != null && e.button !== 0) return;
    e.preventDefault();
    try { canvasRef.current.setPointerCapture(e.pointerId); } catch { /* old browsers */ }
    drawing.current = true; inked.current = false;
    last.current = pos(e);
    const ctx = ctxOf();
    ctx.beginPath(); ctx.arc(last.current.x, last.current.y, ctx.lineWidth / 2, 0, Math.PI * 2); ctx.fillStyle = '#111827'; ctx.fill();
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const p = pos(e); const ctx = ctxOf();
    ctx.beginPath(); ctx.moveTo(last.current.x, last.current.y); ctx.lineTo(p.x, p.y); ctx.stroke();
    last.current = p; inked.current = true;
  };
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    try { update({ image: canvasRef.current.toDataURL('image/png'), method: 'drawn', signedAt: new Date().toISOString() }); } catch { /* tainted canvas — ignore */ }
  };
  const clear = () => {
    const c = canvasRef.current;
    if (c) c.getContext('2d').clearRect(0, 0, c.width, c.height);
    setTyped('');
    update({ image: '', method: undefined, typedText: undefined, signedAt: undefined });
  };
  const applyTyped = (text) => {
    setTyped(text);
    if (!text.trim()) { update({ image: '', method: 'typed', typedText: '', signedAt: undefined }); return; }
    update({ image: renderTypedSignature(text.trim()), method: 'typed', typedText: text, signedAt: new Date().toISOString() });
  };
  const switchMode = (m) => {
    if (m === mode) return;
    setMode(m);
    if (sigRef.current.image) clear();
  };

  const border = `1px solid ${invalid ? 'var(--cw-danger, #dc2626)' : 'var(--cw-input-border, #cbd5e1)'}`;
  const hasInk = !!sig.image;
  return (
    <div id={id} aria-describedby={describedBy}>
      <div className="cw-segment" role="group" aria-label="Signature method" style={{ marginBottom: 8 }}>
        <button type="button" className={mode === 'draw' ? 'active' : ''} aria-pressed={mode === 'draw'} onClick={() => switchMode('draw')}>
          <i className="fa-solid fa-signature" aria-hidden="true" /> Draw
        </button>
        <button type="button" className={mode === 'type' ? 'active' : ''} aria-pressed={mode === 'type'} onClick={() => switchMode('type')}>
          <i className="fa-solid fa-keyboard" aria-hidden="true" /> Type
        </button>
      </div>
      {mode === 'draw' ? (
        <div ref={wrapRef} style={{ position: 'relative', border, borderRadius: 10, background: '#fff', overflow: 'hidden' }}>
          <canvas
            ref={canvasRef}
            role="img"
            aria-label={hasInk ? 'Signature captured' : 'Signature pad — draw with your finger, pen or mouse. Use Type to sign with the keyboard.'}
            style={{ width: '100%', height: SIG_H, touchAction: 'none', cursor: 'crosshair', display: 'block' }}
            onPointerDown={start} onPointerMove={move} onPointerUp={end} onPointerCancel={end} onLostPointerCapture={end}
          />
          {!hasInk && <span aria-hidden="true" style={{ position: 'absolute', top: '50%', left: 0, right: 0, transform: 'translateY(-50%)', textAlign: 'center', color: 'var(--cw-placeholder, #9ca3af)', fontSize: 'var(--cs-text-sm, 0.8125rem)', pointerEvents: 'none' }}>Sign here</span>}
        </div>
      ) : (
        <div>
          <label className="cw-label" htmlFor={`${id || 'sig'}-typed`}>Type your full name to sign</label>
          <input id={`${id || 'sig'}-typed`} className="cw-input" value={typed} onChange={(e) => applyTyped(e.target.value)} autoComplete="name" maxLength={60} />
          {hasInk && <img src={sig.image} alt={`Typed signature: ${typed}`} style={{ marginTop: 8, maxWidth: '100%', height: 80, objectFit: 'contain', border, borderRadius: 10, background: '#fff', display: 'block' }} />}
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(180px, 100%), 1fr))', gap: 10, marginTop: 10 }}>
        <div>
          <label className="cw-label" htmlFor={`${id || 'sig'}-name`}>Signer’s name <span className="cw-req" aria-hidden="true">*</span></label>
          <input id={`${id || 'sig'}-name`} className="cw-input" value={sig.name || ''} onChange={(e) => update({ name: e.target.value })} autoComplete="name" maxLength={80} aria-required="true" aria-invalid={invalid && !(sig.name || '').trim() ? true : undefined} />
        </div>
        <div>
          <label className="cw-label" htmlFor={`${id || 'sig'}-role`}>Role / position</label>
          <input id={`${id || 'sig'}-role`} className="cw-input" value={sig.role || ''} onChange={(e) => update({ role: e.target.value })} placeholder="e.g. Competent person, Site manager" maxLength={80} />
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 6, flexWrap: 'wrap' }}>
        {hasInk && <span className="cw-meta"><i className="fa-solid fa-check" aria-hidden="true" /> Signed {sig.signedAt ? formatDateTime(sig.signedAt) : ''}</span>}
        <button type="button" className="cw-btn-link" onClick={clear} disabled={!hasInk}><i className="fa-solid fa-eraser" aria-hidden="true" /> Clear signature</button>
      </div>
    </div>
  );
};

/* ── Photo annotation ───────────────────────────────────────────────────────
 * Mark up a photo before it's saved: freehand pen, arrow, circle and text, in a
 * few high-contrast colours, with undo. The annotated image replaces the photo. */
const ANNOTATE_COLOURS = [['#dc2626', 'Red'], ['#facc15', 'Yellow'], ['#ffffff', 'White'], ['#111827', 'Black']];
const ANNOTATE_TOOLS = [['pen', 'fa-pen', 'Freehand'], ['arrow', 'fa-arrow-right-long', 'Arrow'], ['circle', 'fa-circle', 'Circle'], ['text', 'fa-font', 'Text']];

const drawShape = (ctx, s, scale) => {
  ctx.strokeStyle = s.colour; ctx.fillStyle = s.colour;
  ctx.lineWidth = 4 * scale; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
  if (s.tool === 'pen') {
    ctx.beginPath();
    s.points.forEach((p, i) => (i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)));
    ctx.stroke();
  } else if (s.tool === 'arrow') {
    const { a, b } = s;
    const ang = Math.atan2(b.y - a.y, b.x - a.x);
    const head = 18 * scale;
    ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(b.x, b.y);
    ctx.lineTo(b.x - head * Math.cos(ang - Math.PI / 7), b.y - head * Math.sin(ang - Math.PI / 7));
    ctx.lineTo(b.x - head * Math.cos(ang + Math.PI / 7), b.y - head * Math.sin(ang + Math.PI / 7));
    ctx.closePath(); ctx.fill();
  } else if (s.tool === 'circle') {
    const rx = Math.abs(s.b.x - s.a.x) / 2; const ry = Math.abs(s.b.y - s.a.y) / 2;
    ctx.beginPath();
    ctx.ellipse((s.a.x + s.b.x) / 2, (s.a.y + s.b.y) / 2, Math.max(rx, 1), Math.max(ry, 1), 0, 0, Math.PI * 2);
    ctx.stroke();
  } else if (s.tool === 'text') {
    const size = 28 * scale;
    ctx.font = `bold ${size}px system-ui, -apple-system, Segoe UI, sans-serif`;
    ctx.textBaseline = 'top';
    ctx.lineWidth = 4 * scale;
    ctx.strokeStyle = s.colour === '#ffffff' || s.colour === '#facc15' ? '#111827' : '#ffffff';
    ctx.strokeText(s.text, s.a.x, s.a.y);
    ctx.fillText(s.text, s.a.x, s.a.y);
  }
};

const PhotoAnnotator = ({ src, onSave, onClose }) => {
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const [shapes, setShapes] = useState([]);
  const [tool, setTool] = useState('arrow');
  const [colour, setColour] = useState('#dc2626');
  const [text, setText] = useState('');
  const live = useRef(null);
  const [ready, setReady] = useState(false);

  const redraw = useCallback((extra) => {
    const c = canvasRef.current; const img = imgRef.current;
    if (!c || !img) return;
    const ctx = c.getContext('2d');
    ctx.clearRect(0, 0, c.width, c.height);
    ctx.drawImage(img, 0, 0, c.width, c.height);
    const scale = c.width / 1000;
    [...shapes, ...(extra ? [extra] : [])].forEach((s) => drawShape(ctx, s, Math.max(scale, 0.6)));
  }, [shapes]);

  useEffect(() => {
    let alive = true;
    loadImage(src).then((img) => {
      if (!alive || !canvasRef.current) return;
      imgRef.current = img;
      canvasRef.current.width = img.naturalWidth; canvasRef.current.height = img.naturalHeight;
      setReady(true);
    }).catch(() => toast.error('Couldn’t open that photo for markup'));
    return () => { alive = false; };
  }, [src]);
  useEffect(() => { if (ready) redraw(); }, [ready, redraw]);

  const pos = (e) => {
    const c = canvasRef.current; const r = c.getBoundingClientRect();
    return { x: (e.clientX - r.left) * (c.width / r.width), y: (e.clientY - r.top) * (c.height / r.height) };
  };
  const down = (e) => {
    if (!ready) return;
    e.preventDefault();
    const p = pos(e);
    if (tool === 'text') {
      if (!text.trim()) { toast.info('Type the label text first, then tap where it goes'); return; }
      setShapes((s) => [...s, { tool: 'text', colour, text: text.trim(), a: p }]);
      return;
    }
    try { canvasRef.current.setPointerCapture(e.pointerId); } catch { /* ignore */ }
    live.current = tool === 'pen' ? { tool, colour, points: [p] } : { tool, colour, a: p, b: p };
  };
  const moveP = (e) => {
    if (!live.current) return;
    e.preventDefault();
    const p = pos(e);
    if (live.current.tool === 'pen') live.current = { ...live.current, points: [...live.current.points, p] };
    else live.current = { ...live.current, b: p };
    redraw(live.current);
  };
  const up = () => {
    if (!live.current) return;
    const s = live.current; live.current = null;
    setShapes((prev) => [...prev, s]);
  };
  const save = () => {
    try { onSave(canvasRef.current.toDataURL('image/jpeg', 0.85)); } catch { toast.error('Couldn’t save the marked-up photo'); }
  };

  return (
    <Modal
      title="Mark up photo"
      description="Draw arrows, circles, freehand or text to point out the defect. Saving replaces the photo with the marked-up version."
      maxWidth={900}
      dirty={shapes.length > 0}
      onClose={onClose}
      footer={({ requestClose }) => (
        <>
          <button type="button" className="cw-btn cw-btn-secondary" onClick={requestClose}>Cancel</button>
          <button type="button" className="cw-btn cw-btn-primary" onClick={save} disabled={!ready}><i className="fa-solid fa-floppy-disk" aria-hidden="true" /> Save markup</button>
        </>
      )}
    >
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
        <div className="cw-segment" role="group" aria-label="Markup tool">
          {ANNOTATE_TOOLS.map(([id, icon, label]) => (
            <button key={id} type="button" className={tool === id ? 'active' : ''} aria-pressed={tool === id} onClick={() => setTool(id)}>
              <i className={`fa-solid ${icon}`} aria-hidden="true" /> {label}
            </button>
          ))}
        </div>
        <div role="group" aria-label="Colour" style={{ display: 'flex', gap: 6 }}>
          {ANNOTATE_COLOURS.map(([c, name]) => (
            <button key={c} type="button" onClick={() => setColour(c)} aria-pressed={colour === c} aria-label={`${name} ink`} title={name}
              style={{ width: 32, height: 32, borderRadius: '50%', background: c, border: colour === c ? '3px solid var(--cw-blue, #1A80E5)' : '1px solid var(--cw-border-strong, #9ca3af)', cursor: 'pointer' }} />
          ))}
        </div>
        <button type="button" className="cw-btn cw-btn-secondary" onClick={() => setShapes((s) => s.slice(0, -1))} disabled={!shapes.length}><i className="fa-solid fa-rotate-left" aria-hidden="true" /> Undo</button>
      </div>
      {tool === 'text' && (
        <Field label="Label text" hint="Type the label, then tap the photo where it should go.">
          <input value={text} onChange={(e) => setText(e.target.value)} maxLength={40} placeholder="e.g. Corroded fixing" />
        </Field>
      )}
      <canvas
        ref={canvasRef}
        role="img"
        aria-label={`Photo with ${shapes.length} mark${shapes.length === 1 ? '' : 's'}`}
        onPointerDown={down} onPointerMove={moveP} onPointerUp={up} onPointerCancel={up}
        style={{ width: '100%', height: 'auto', maxHeight: '60vh', objectFit: 'contain', display: 'block', touchAction: 'none', cursor: 'crosshair', borderRadius: 8, background: 'var(--cw-surface-alt, #f3f4f6)' }}
      />
    </Modal>
  );
};

const PhotoInput = ({ value, onChange, invalid, id, describedBy, label }) => {
  const photos = photoList(value);
  const [busy, setBusy] = useState(false);
  const [annotating, setAnnotating] = useState(null);   // index
  const inputRef = useRef(null);
  const left = MAX_PHOTOS_PER_FIELD - photos.length;

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    const take = files.slice(0, Math.max(0, left));
    if (files.length > take.length) toast.info(`Only ${MAX_PHOTOS_PER_FIELD} photos per question — ${files.length - take.length} not added`);
    setBusy(true);
    const added = [];
    for (const f of take) {
      if (!/^image\//.test(f.type) && !/\.(jpe?g|png|webp|heic|heif|gif)$/i.test(f.name)) { toast.error(`${f.name} isn’t an image`); continue; }
      try { added.push(await compressImage(f)); } catch { toast.error(`Couldn’t read ${f.name} — try a JPEG or PNG`); }
    }
    setBusy(false);
    if (added.length) onChange([...photos, ...added]);
  };
  const remove = (i) => onChange(photos.filter((_, j) => j !== i));
  const replace = (i, src) => onChange(photos.map((p, j) => (j === i ? src : p)));

  return (
    <div id={id} aria-describedby={describedBy}>
      {photos.length > 0 && (
        <ul style={{ listStyle: 'none', margin: '0 0 8px', padding: 0, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(120px, 100%), 1fr))', gap: 8 }}>
          {photos.map((p, i) => (
            <li key={i} style={{ border: '1px solid var(--cw-border)', borderRadius: 10, overflow: 'hidden', background: 'var(--cw-surface-alt, #f9fafb)' }}>
              <img src={p} alt={`${label || 'Photo'} ${i + 1} of ${photos.length}`} style={{ width: '100%', height: 96, objectFit: 'cover', display: 'block' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: 2 }}>
                <button type="button" className="cw-icon-btn" aria-label={`Mark up photo ${i + 1}`} title="Mark up" onClick={() => setAnnotating(i)}><i className="fa-solid fa-pen" aria-hidden="true" /></button>
                <button type="button" className="cw-icon-btn" aria-label={`Remove photo ${i + 1}`} title="Remove" onClick={() => remove(i)}><i className="fa-solid fa-trash" aria-hidden="true" style={{ color: 'var(--cw-danger-fg, #b91c1c)' }} /></button>
              </div>
            </li>
          ))}
        </ul>
      )}
      {left > 0 && (
        <button type="button" onClick={() => inputRef.current && inputRef.current.click()} disabled={busy}
          style={{ width: '100%', minHeight: 64, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4, border: `1px dashed ${invalid ? 'var(--cw-danger, #dc2626)' : 'var(--cw-input-border, #cbd5e1)'}`, borderRadius: 10, padding: 14, color: 'var(--cw-text-muted)', background: 'var(--cw-surface, #fff)', fontSize: 'var(--cs-text-sm, 0.8125rem)', cursor: 'pointer', font: 'inherit' }}>
          <i className="fa-solid fa-camera" aria-hidden="true" style={{ fontSize: '1.1rem' }} />
          {busy ? 'Processing photos…' : (photos.length ? `Add another photo (${photos.length}/${MAX_PHOTOS_PER_FIELD})` : 'Take photo / choose photos')}
        </button>
      )}
      <input ref={inputRef} type="file" accept="image/*" capture="environment" multiple tabIndex={-1} aria-hidden="true" style={{ display: 'none' }} onChange={onFiles} />
      {annotating != null && photos[annotating] && (
        <PhotoAnnotator src={photos[annotating]} onClose={() => setAnnotating(null)} onSave={(src) => { replace(annotating, src); setAnnotating(null); }} />
      )}
    </div>
  );
};

const FileInput = ({ value, onChange, invalid, id, describedBy }) => {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const onFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > FILE_MAX_BYTES) { toast.error(`That file is ${(file.size / 1048576).toFixed(1)} MB — the limit is ${FILE_MAX_BYTES / 1048576} MB`); return; }
    setBusy(true);
    const reader = new FileReader();
    reader.onload = () => { onChange(`${reader.result}#name=${encodeURIComponent(file.name)}`); setBusy(false); };
    reader.onerror = () => { toast.error('Could not read that file'); setBusy(false); };
    reader.readAsDataURL(file);
  };
  const named = fileNameOf(value);
  return (
    <div id={id} aria-describedby={describedBy}>
      {value && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, fontSize: 'var(--cs-text-sm, 0.8125rem)' }}>
          <i className="fa-solid fa-paperclip" aria-hidden="true" style={{ color: 'var(--cw-blue)' }} />
          <span style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{named || 'Attached file'}</span>
          <button type="button" className="cw-btn-link" onClick={() => onChange('')}>Remove</button>
        </div>
      )}
      <button type="button" onClick={() => inputRef.current && inputRef.current.click()} disabled={busy}
        style={{ width: '100%', minHeight: 64, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4, border: `1px dashed ${invalid ? 'var(--cw-danger, #dc2626)' : 'var(--cw-input-border, #cbd5e1)'}`, borderRadius: 10, padding: 14, color: 'var(--cw-text-muted)', background: 'var(--cw-surface, #fff)', font: 'inherit', fontSize: 'var(--cs-text-sm, 0.8125rem)', cursor: 'pointer' }}>
        <i className="fa-solid fa-paperclip" aria-hidden="true" style={{ fontSize: '1.1rem' }} />
        {busy ? 'Reading…' : (value ? 'Choose a different file' : `Attach a file (PDF, document, image — up to ${FILE_MAX_BYTES / 1048576} MB)`)}
      </button>
      <input ref={inputRef} type="file" tabIndex={-1} aria-hidden="true" style={{ display: 'none' }} onChange={onFile} />
    </div>
  );
};

const FieldInput = ({ field, value, onChange, invalid, id, 'aria-describedby': describedBy, 'aria-required': ariaRequired }) => {
  const set = (v) => onChange(v);
  const a11y = { id, 'aria-describedby': describedBy, 'aria-invalid': invalid || undefined, 'aria-required': ariaRequired || undefined };
  const cls = 'cw-input';
  switch (field.type) {
    case 'Number':
      return <input {...a11y} type="number" inputMode="decimal" step="any" className={cls} value={value ?? ''} onChange={(e) => set(e.target.value)} placeholder="0" />;
    case 'Date':
      return <input {...a11y} type="date" className={cls} value={value || ''} onChange={(e) => set(e.target.value)} />;
    case 'Yes / No':
      return (
        <div id={id} role="group" aria-describedby={describedBy} style={{ display: 'flex', gap: 8 }}>
          {['Yes', 'No'].map((o) => (
            <button key={o} type="button" onClick={() => set(value === o ? '' : o)} aria-pressed={value === o}
              className={`cw-btn ${value === o ? (o === 'Yes' ? 'cw-btn-success' : 'cw-btn-primary') : 'cw-btn-secondary'}`}
              style={{ flex: 1, justifyContent: 'center', ...(invalid && value !== 'Yes' && value !== 'No' ? { borderColor: 'var(--cw-danger, #dc2626)' } : {}) }}>
              {value === o && <i className="fa-solid fa-check" aria-hidden="true" />} {o}
            </button>
          ))}
        </div>
      );
    case 'Dropdown': {
      const opts = normOptions(field.options);
      return (
        <select {...a11y} className={cls} value={value || ''} onChange={(e) => set(e.target.value)}>
          <option value="">Select…</option>
          {opts.length ? opts.map((o) => <option key={o} value={o}>{o}</option>)
            : <option value="" disabled>No options defined</option>}
        </select>
      );
    }
    case 'Checklist':
    case 'Multi-select': {
      const opts = normOptions(field.options);
      const arr = Array.isArray(value) ? value : [];
      const toggle = (o) => set(arr.includes(o) ? arr.filter((x) => x !== o) : [...arr, o]);
      return (
        <div id={id} role="group" aria-describedby={describedBy} style={{ display: 'flex', flexDirection: 'column', gap: 2, border: `1px solid ${invalid ? 'var(--cw-danger, #dc2626)' : 'var(--cw-border-light, #f3f4f6)'}`, borderRadius: 8, padding: opts.length ? '4px 10px' : 0 }}>
          {opts.length ? opts.map((o) => (
            <label key={o} style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 40, fontSize: 'var(--cs-text-sm, 0.8125rem)', cursor: 'pointer' }}>
              <input type="checkbox" checked={arr.includes(o)} onChange={() => toggle(o)} style={{ width: 18, height: 18, accentColor: 'var(--cw-blue, #1A80E5)' }} /> {o}
            </label>
          )) : <span className="cw-meta">No checklist items defined</span>}
        </div>
      );
    }
    case 'Photo':
      return <PhotoInput value={value} onChange={set} invalid={invalid} id={id} describedBy={describedBy} label={field.label} />;
    case 'File':
      return <FileInput value={value} onChange={set} invalid={invalid} id={id} describedBy={describedBy} />;
    case 'Signature':
      return <SignaturePad value={value} onChange={set} invalid={invalid} id={id} describedBy={describedBy} />;
    case REPEATING_TYPE:
      return <RepeatingListInput field={field} value={value} onChange={set} invalid={invalid} id={id} describedBy={describedBy} />;
    default: // Text
      return <textarea {...a11y} className={cls} style={{ minHeight: 44, resize: 'vertical', fontFamily: 'inherit' }} value={value || ''} onChange={(e) => set(e.target.value)} placeholder="Your answer" />;
  }
};

/**
 * <FormPreview /> — renders a template's fields exactly as the fill-out form does:
 * section headings, conditional visibility, calculated + graded fields, and every input
 * type. Controlled — the parent supplies `values` (keyed by field id) and
 * `onFieldChange(fieldId, value)`. Reused by <FormFill /> and by the form builder's live
 * preview, so the preview is guaranteed to match the real form.
 *
 * `errors` is { [fieldId]: message } — shown inline under the field.
 */
export const FormPreview = ({ fields: rawFields, values = {}, onFieldChange, errors = {}, bands = DEFAULT_BANDS, flags = {}, onFlagChange, priorityLevels = DEFAULT_PRIORITY_LEVELS }) => {
  // Builder fields may not have been normalised yet; ids must exist to key values.
  const fields = useMemo(() => (rawFields.every((f) => f && f.id) ? rawFields : normaliseFields(rawFields)), [rawFields]);
  return (
    <>
      {fields.map((f, i) => {
        if (f.type === 'Section') {
          return <h3 key={f.id} style={{ margin: i === 0 ? '0 0 8px' : '18px 0 8px', paddingTop: i === 0 ? 0 : 12, borderTop: i === 0 ? 'none' : '1px solid var(--cw-border)', fontWeight: 700, fontSize: 'var(--cs-text-md, 0.9375rem)', color: 'var(--cw-text)' }}>{f.label || 'Section'}</h3>;
        }
        if (!fieldVisible(f, fields, values)) return null;                       // conditional: hidden until its rule is met
        if (f.type === 'Calculated') {
          const computed = evalFormula(f.formula, fields, values);
          const grade = f.grade && computed !== '' ? gradeFor(computed, bands) : '';
          return (
            <div key={f.id} className="cw-field" data-field-id={f.id}>
              <span className="cw-label">{f.label || `Field ${i + 1}`}</span>
              <output aria-live="polite" style={{ ...inputStyle, background: 'var(--cw-surface-alt, #f9fafb)', color: 'var(--cw-text)', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
                <i className={`fa-solid ${f.grade ? 'fa-award' : 'fa-calculator'}`} aria-hidden="true" style={{ color: 'var(--cw-text-faint)' }} />
                {computed === '' ? '—' : computed}
                {grade && <span className="cw-chip" style={{ marginLeft: 6, color: 'var(--cw-success-fg)', background: 'var(--cw-success-tint)' }}>Grade: {grade}</span>}
              </output>
            </div>
          );
        }
        const flagged = isFlagged(f, values[f.id]);
        const flag = flags[f.id] || {};
        const err = errors[f.id];
        return (
          <div key={f.id} data-field-id={f.id}>
            <Field label={f.label || `Field ${i + 1}`} required={f.required} error={err && !flagged ? err : undefined} hint={f.hint}>
              <FieldInput field={f} value={values[f.id]} invalid={!!err} onChange={(v) => onFieldChange && onFieldChange(f.id, v)} />
            </Field>
            {/* This answer is a finding — capture its priority now, on the spot. */}
            {flagged && (
              <div className="cw-finding" style={{ marginTop: -6, marginBottom: 14 }}>
                <div className="hd"><i className="fa-solid fa-triangle-exclamation" aria-hidden="true" /> Finding raised — set a priority</div>
                <div className="lv" role="group" aria-label={`Priority for ${f.label || 'this finding'}`}>
                  {priorityLevels.map((l) => {
                    const on = flag.priority === l.code;
                    return (
                      <button key={l.code} type="button" aria-pressed={on}
                        className={`cw-prio${on ? ' on' : ''}`}
                        style={on ? { background: l.colour, borderColor: l.colour, color: '#fff' } : { borderColor: l.colour, color: l.colour }}
                        onClick={() => onFlagChange && onFlagChange(f.id, { ...flag, priority: on ? null : l.code })}>
                        {on && <i className="fa-solid fa-check" aria-hidden="true" />} {l.label}
                      </button>
                    );
                  })}
                </div>
                <label className="cw-sr-only" htmlFor={`finding-${f.id}`}>Finding detail for {f.label}</label>
                <textarea
                  id={`finding-${f.id}`}
                  value={flag.note || ''}
                  placeholder="What's wrong, and what needs doing?"
                  onChange={(e) => onFlagChange && onFlagChange(f.id, { ...flag, note: e.target.value })}
                />
                {err && <p className="cw-field-error" role="alert"><i className="fa-solid fa-circle-exclamation" aria-hidden="true" /> {err}</p>}
              </div>
            )}
          </div>
        );
      })}
    </>
  );
};

/** Turn the current values into the stored answers array (visible fields only). */
export const buildAnswers = (fields, values, flags = {}, bands = DEFAULT_BANDS) => fields
  .filter((f) => f.type !== 'Section' && fieldVisible(f, fields, values))
  .map((f, idx) => {
    const raw = values[f.id];
    let val;
    let extra = {};
    if (f.type === 'Calculated') {
      const score = evalFormula(f.formula, fields, values);
      val = f.grade ? (score === '' ? '' : `${score} (${gradeFor(score, bands)})`) : score;
    } else if (f.type === 'Photo') {
      val = photoList(raw);
    } else if (f.type === 'Signature') {
      const s = sigOf(raw);
      val = s.image || '';
      if (s.image) extra = { signer: { name: (s.name || '').trim(), role: (s.role || '').trim(), signedAt: s.signedAt || null, method: s.method || 'drawn' } };
    } else if (f.type === REPEATING_TYPE) {
      // The value stays readable text (PDFs, viewers); rows + columns carry the data
      // the server reports on and maps into the asset register.
      const cols = columnsOf(f);
      val = repeatingSummary(raw, cols);
      extra = { rows: filledRows(raw, cols), columns: cols.map((c) => ({ id: c.id, label: c.label, type: c.type, ...(c.key ? { key: c.key } : {}) })) };
    } else val = displayValue(raw);
    const flag = isFlagged(f, raw) ? (flags[f.id] || {}) : null;
    return {
      fieldId: f.id, label: f.label || `Field ${idx + 1}`, type: f.type, value: val, ...extra,
      // A flagged answer is a finding: it carries its priority and note so it can
      // be counted, reported and chased rather than read out of prose.
      ...(flag ? { flagged: true, priority: flag.priority || null, priorityNote: flag.note || '' } : {}),
    };
  });

/** Validate visible required fields + findings. Returns { [fieldId]: message }. */
const validate = (fields, values, flags) => {
  const errs = {};
  fields.forEach((f) => {
    if (f.type === 'Section' || f.type === 'Calculated' || !fieldVisible(f, fields, values)) return;
    const v = values[f.id];
    if (f.type === 'Signature') {
      const s = sigOf(v);
      if (f.required && !s.image) errs[f.id] = 'Sign here, and add the signer’s name';
      else if (s.image && !(s.name || '').trim()) errs[f.id] = 'Add the signer’s name';
      return;
    }
    if (f.required && isEmptyFor(f, v)) { errs[f.id] = f.type === 'Photo' ? 'Add at least one photo' : f.type === REPEATING_TYPE ? 'Add at least one row' : 'Answer this question'; return; }
    if (f.type === 'Number' && v !== '' && v != null && !Number.isFinite(Number(v))) { errs[f.id] = 'Enter a number'; return; }
    if (isFlagged(f, v) && !(flags[f.id] && flags[f.id].priority)) errs[f.id] = 'Set a priority for this finding';
  });
  return errs;
};

/**
 * <FormFill /> — renders a template's fields as an on-site fill-out form and submits
 * the answers. Supports section headings, inline required validation, multiple photos
 * with markup, drawn or typed signatures (with signer name, role and time), and context
 * auto-fill (job/site/date/engineer).
 *
 * Unsaved changes are tracked internally (any answer, photo, signature or finding),
 * so Escape / backdrop / Cancel ask before discarding; a parent may also pass `dirty`.
 *
 * onSubmit(values, fields, answers) is provided by the parent. Values are keyed by
 * field id. The parent should throw (or return false) when saving fails; the form then
 * stays open with everything intact.
 *
 * An in-progress fill is kept as a per-user draft on this device (cleared on submit or
 * discard). The key includes the submission id, so editing a past submission never
 * leaks into a new fill of the same template.
 */
const draftKey = (template, context, submissionId) => `form_draft_${template?.id ?? template?.name ?? 'x'}_${(context && (context.jobId || context.job)) || ''}_${submissionId ?? 'new'}`;

const FormFill = ({ template, onClose, onSubmit, context, initialValues, submitLabel, submissionId, dirty: dirtyProp = false }) => {
  const fields = useMemo(() => parseFields(template), [template]);
  const subId = submissionId ?? context?.sub ?? null;
  const DKEY = draftKey(template, context, subId);
  const [restored] = useState(() => {
    const d = storageGet(DKEY, null);
    return d && typeof d === 'object' && d.values ? d : null;
  });
  const [values, setValues] = useState(() => {
    if (restored) return rekeyValues(restored.values, fields);
    if (initialValues && typeof initialValues === 'object') return rekeyValues(initialValues, fields);
    return seedFromContext(fields, context);
  });
  const [flags, setFlags] = useState(() => {
    if (restored && restored.flags) return rekeyValues(restored.flags, fields);
    return rekeyValues((initialValues && initialValues.__flags) || {}, fields);
  });
  const [dirty, setDirty] = useState(!!restored);
  const [errors, setErrors] = useState({});
  const [focusTick, setFocusTick] = useState(0);
  const [saving, setSaving] = useState(false);
  const [bands, setBands] = useState(DEFAULT_BANDS);
  const [priorityLevels, setPriorityLevels] = useState(DEFAULT_PRIORITY_LEVELS);
  const bodyRef = useRef(null);
  const summaryRef = useRef(null);
  // Set when onSubmit resolved without failing. The parent closes the form on success;
  // when it unmounts after that, the device draft is no longer needed.
  const submittedRef = useRef(false);
  useEffect(() => () => { if (submittedRef.current) storageRemove(DKEY); }, [DKEY]);

  // Persist the draft as it's filled (only once something has changed).
  useEffect(() => {
    if (!dirty) return;
    storageSet(DKEY, { values, flags, savedAt: new Date().toISOString() });
  }, [values, flags, dirty, DKEY]);

  // Clients grade differently (A–E, 1–5, 1–4 with residual), so use theirs when set.
  useEffect(() => {
    const clientId = context?.clientId;
    if (!clientId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const d = await apiFetch(`/api/contractor/clients/${clientId}/scales/priority`);
        const levels = d?.scale?.levels;
        if (!cancelled && Array.isArray(levels) && levels.length) {
          setPriorityLevels(levels.map((l) => ({ code: l.code || l.label, label: l.label || l.code, colour: l.colour || '#4b5563' })));
        }
      } catch { /* keep the default 1–4 */ }
    })();
    return () => { cancelled = true; };
  }, [context?.clientId]);

  // If the form has a grade field and we know the client, pull that client's grade scale.
  const needsGrade = fields.some((f) => f.type === 'Calculated' && f.grade);
  useEffect(() => {
    if (!needsGrade || !context?.client) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const d = await apiFetch('/api/contractor/settings');
        const scales = Array.isArray(d?.data?.gradingScales) ? d.data.gradingScales : [];
        const match = scales.find((s) => String(s.client || '').trim().toLowerCase() === String(context.client).trim().toLowerCase());
        if (!cancelled && match && Array.isArray(match.bands) && match.bands.length) setBands(match.bands);
      } catch { /* keep default bands */ }
    })();
    return () => { cancelled = true; };
  }, [needsGrade, context?.client]);

  // After a failed submit, move to the first problem.
  useEffect(() => {
    if (!focusTick) return;
    const first = fields.find((f) => errors[f.id]);
    if (!first || !bodyRef.current) return;
    const el = bodyRef.current.querySelector(`[data-field-id="${first.id}"]`);
    if (!el) return;
    el.scrollIntoView({ block: 'center', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    const target = el.querySelector('[aria-invalid="true"], input:not([type="hidden"]):not([tabindex="-1"]), select, textarea, button');
    if (target) setTimeout(() => { try { target.focus({ preventScroll: true }); } catch { /* ignore */ } }, prefersReducedMotion() ? 0 : 250);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusTick]);

  const clearError = (id) => setErrors((e) => { if (!e[id]) return e; const n = { ...e }; delete n[id]; return n; });

  const close = () => { storageRemove(DKEY); if (onClose) onClose(); };
  const discardDraft = () => {
    storageRemove(DKEY);
    setValues(initialValues && typeof initialValues === 'object' ? rekeyValues(initialValues, fields) : seedFromContext(fields, context));
    setFlags(rekeyValues((initialValues && initialValues.__flags) || {}, fields));
    setDirty(false); setErrors({});
  };

  const submit = useCallback(async () => {
    const errs = validate(fields, values, flags);
    setErrors(errs);
    if (Object.keys(errs).length) {
      setFocusTick((t) => t + 1);
      return;
    }
    const answers = buildAnswers(fields, values, flags, bands);
    const data = { ...values, __flags: flags };
    let size = 0;
    try { size = JSON.stringify({ data, answers }).length; } catch { size = 0; }
    if (size > MAX_SUBMISSION_BYTES) {
      toast.error(`This form is about ${Math.round(size / 1048576)} MB with its photos — the limit is ${Math.round(MAX_SUBMISSION_BYTES / 1048576)} MB. Remove a few photos and try again.`);
      return;
    }
    setSaving(true);
    try {
      const r = await onSubmit?.(data, fields, answers);
      submittedRef.current = r !== false;
    } catch { submittedRef.current = false; /* the parent has already shown the error */ } finally { setSaving(false); }
  }, [fields, values, flags, bands, onSubmit]);   // flags/bands feed the answers — a stale closure would drop findings

  const errorList = fields.filter((f) => errors[f.id]);

  return (
    <Modal
      title={`Fill: ${template.name}`}
      description={template.description || undefined}
      maxWidth={640}
      dirty={dirty || !!dirtyProp}
      onClose={close}
      footer={({ requestClose }) => (
        <>
          <button type="button" className="cw-btn cw-btn-secondary" onClick={requestClose} disabled={saving}>Cancel</button>
          <button type="button" className="cw-btn cw-btn-primary" onClick={submit} disabled={saving} aria-busy={saving || undefined}>
            <i className={`fa-solid ${saving ? 'fa-spinner fa-spin' : 'fa-paper-plane'}`} aria-hidden="true" /> {saving ? 'Saving…' : (submitLabel || 'Save & submit')}
          </button>
        </>
      )}
    >
      <div ref={bodyRef}>
        {restored && dirty && (
          <div className="cw-meta" role="status" style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', padding: '8px 12px', marginBottom: 12, borderRadius: 8, background: 'var(--cw-blue-tint, #EBF4FF)' }}>
            <i className="fa-solid fa-clock-rotate-left" aria-hidden="true" />
            <span>Restored your unsaved answers from {formatDateTime(restored.savedAt)} (kept on this device only).</span>
            <button type="button" className="cw-btn-link" onClick={discardDraft}>Start again</button>
          </div>
        )}
        {errorList.length > 0 && (
          <div ref={summaryRef} role="alert" className="cw-error-state compact" style={{ display: 'block', marginBottom: 14 }}>
            <p className="cw-error-state-title" style={{ margin: '0 0 4px' }}>
              {errorList.length === 1 ? '1 question needs attention' : `${errorList.length} questions need attention`}
            </p>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {errorList.map((f) => (
                <li key={f.id}>
                  <button type="button" className="cw-btn-link" onClick={() => { const el = bodyRef.current?.querySelector(`[data-field-id="${f.id}"]`); if (el) { el.scrollIntoView({ block: 'center' }); const t = el.querySelector('input, select, textarea, button'); if (t) t.focus({ preventScroll: true }); } }}>
                    {f.label || 'Untitled question'}: {errors[f.id]}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
        <FormPreview
          fields={fields}
          values={values}
          errors={errors}
          bands={bands}
          onFieldChange={(id, v) => { setValues((s) => ({ ...s, [id]: v })); setDirty(true); submittedRef.current = false; clearError(id); }}
          flags={flags}
          priorityLevels={priorityLevels}
          onFlagChange={(id, next) => { setFlags((f) => ({ ...f, [id]: next })); setDirty(true); submittedRef.current = false; clearError(id); }}
        />
      </div>
    </Modal>
  );
};

export default FormFill;
